import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  Loader2,
  Maximize2,
  Minimize2,
  Pause,
  PictureInPicture2,
  Play,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/cn";

export type VideoPlayerProps = {
  /** Presigned video URL. Changing this resets state to the new source. */
  src: string;
  /** Persists currentTime across sessions. See AudioPlayer for the format. */
  progressKey?: string;
  /** Slot rendered ABOVE the video container -- e.g. a language switcher. */
  topSlot?: React.ReactNode;
  /** Called when the underlying <video> fires an unrecoverable error. */
  onError?: () => void;
  /** Every `timeupdate`, with the current position and total duration. Lets the
   *  caller measure how much was actually played (see useMediaCompletion). */
  onPlaybackProgress?: (info: { currentTime: number; duration: number }) => void;
  className?: string;
};

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2] as const;
const SAVE_EVERY_SECONDS = 5;
const FULLSCREEN_IDLE_MS = 2800;

type DocumentWithPip = Document & {
  pictureInPictureElement?: Element | null;
  exitPictureInPicture?: () => Promise<void>;
};
type VideoWithPip = HTMLVideoElement & {
  requestPictureInPicture?: () => Promise<unknown>;
  disablePictureInPicture?: boolean;
};
/** iOS Safari (iPhone) has no Fullscreen API on arbitrary elements -- only
 * this legacy webkit method on the <video> itself puts it into the native
 * fullscreen player. iPadOS does support the standard API. */
type VideoWithWebkitFullscreen = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
  webkitDisplayingFullscreen?: boolean;
};

/**
 * Custom <video> shell with a YouTube-style overlay. Built to match v1's
 * full feature set (skip +-10s, seek with buffered indicator, volume +
 * mute, playback rate menu, fullscreen, keyboard shortcuts, resume
 * position) plus a couple of v2-era additions (Picture-in-Picture,
 * polished Pulse-themed motion). Auto-hides controls 2.8s after the
 * pointer goes idle while playing -- both fullscreen AND inline -- and
 * tap-to-toggle on mobile.
 */
export function VideoPlayer({ src, progressKey, topSlot, onError, onPlaybackProgress, className }: VideoPlayerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const videoRef     = useRef<HTMLVideoElement | null>(null);
  const idleTimerRef = useRef<number | null>(null);
  const lastSavedRef = useRef<number>(0);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [bufferedEnd, setBufferedEnd] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [rate, setRate] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isBuffering, setIsBuffering] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPip, setIsPip] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [rateOpen, setRateOpen] = useState(false);
  /** Visual feedback for double-tap skip. Increments each time. */
  const [skipFlash, setSkipFlash] = useState<{ key: number; dir: "back" | "forward" } | null>(null);

  // Double-tap detection state. Kept in refs so the handlers don't
  // pull stale closures.
  const lastTapRef = useRef<{ at: number; x: number; side: "left" | "right" | "center" } | null>(null);
  const singleTapTimerRef = useRef<number | null>(null);

  const storageKey = useMemo(
    () => (progressKey ? `staxed:video-progress:${progressKey}` : null),
    [progressKey],
  );

  // ----------------------------------------------------------------------
  // Source change -- reload, restore saved position.
  // ----------------------------------------------------------------------
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    setIsPlaying(false);
    setCurrentTime(0);
    setBufferedEnd(0);
    setDuration(0);
    setIsLoading(true);
    setRateOpen(false);
    lastSavedRef.current = 0;

    if (storageKey) {
      const saved = parseFloat(localStorage.getItem(storageKey) ?? "");
      if (Number.isFinite(saved) && saved > 0) {
        const apply = () => { if (videoRef.current) videoRef.current.currentTime = saved; };
        if (v.readyState >= 1) apply();
        else v.addEventListener("loadedmetadata", apply, { once: true });
      }
    }
  }, [src, storageKey]);

  // ----------------------------------------------------------------------
  // Fullscreen + PiP listeners.
  // ----------------------------------------------------------------------
  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  // iPhone Safari never sets document.fullscreenElement for video fullscreen --
  // it fires these events on the <video> element instead.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onBegin = () => setIsFullscreen(true);
    const onEnd = () => setIsFullscreen(false);
    v.addEventListener("webkitbeginfullscreen", onBegin);
    v.addEventListener("webkitendfullscreen", onEnd);
    return () => {
      v.removeEventListener("webkitbeginfullscreen", onBegin);
      v.removeEventListener("webkitendfullscreen", onEnd);
    };
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const enter = () => setIsPip(true);
    const leave = () => setIsPip(false);
    v.addEventListener("enterpictureinpicture", enter);
    v.addEventListener("leavepictureinpicture", leave);
    return () => {
      v.removeEventListener("enterpictureinpicture", enter);
      v.removeEventListener("leavepictureinpicture", leave);
    };
  }, []);

  // ----------------------------------------------------------------------
  // Auto-hide controls when playing + pointer idle.
  // ----------------------------------------------------------------------
  const bumpIdle = useCallback(() => {
    setControlsVisible(true);
    if (idleTimerRef.current) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (isPlaying) {
      idleTimerRef.current = window.setTimeout(
        () => setControlsVisible(false),
        FULLSCREEN_IDLE_MS,
      );
    }
  }, [isPlaying]);

  useEffect(() => {
    bumpIdle();
    return () => {
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
    };
  }, [bumpIdle]);

  // ----------------------------------------------------------------------
  // Controls handlers
  // ----------------------------------------------------------------------
  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) void v.play().catch(() => onError?.());
    else v.pause();
  }, [onError]);

  const skip = useCallback((seconds: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, Math.min((duration || v.duration || 0), v.currentTime + seconds));
  }, [duration]);

  const seek = useCallback((value: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = value;
    setCurrentTime(value);
  }, []);

  const setVol = useCallback((v: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.volume = v;
    video.muted = v === 0;
    setVolume(v);
    setIsMuted(v === 0);
  }, []);

  const toggleMute = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (isMuted) {
      const restore = volume > 0 ? volume : 0.6;
      v.muted = false;
      v.volume = restore;
      setVolume(restore);
      setIsMuted(false);
    } else {
      v.muted = true;
      setIsMuted(true);
    }
  }, [isMuted, volume]);

  const changeRate = useCallback((r: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.playbackRate = r;
    setRate(r);
    setRateOpen(false);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    const v = videoRef.current as VideoWithWebkitFullscreen | null;

    // iPhone Safari has no requestFullscreen() on arbitrary elements, only
    // the legacy webkit API on the <video> itself (iPad Safari does support
    // the standard API, so this only kicks in where the standard one is
    // truly unavailable).
    if (!el?.requestFullscreen && v?.webkitEnterFullscreen) {
      if (v.webkitDisplayingFullscreen) v.webkitExitFullscreen?.();
      else v.webkitEnterFullscreen();
      return;
    }

    if (!el) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      el.requestFullscreen().catch(() => {
        // Standard API exists but rejected (seen on some iOS versions) --
        // fall back to the native video player.
        v?.webkitEnterFullscreen?.();
      });
    }
  }, []);

  const togglePip = useCallback(async () => {
    const v = videoRef.current as VideoWithPip | null;
    const d = document as DocumentWithPip;
    if (!v) return;
    try {
      if (d.pictureInPictureElement) {
        await d.exitPictureInPicture?.();
      } else if (v.requestPictureInPicture && !v.disablePictureInPicture) {
        await v.requestPictureInPicture();
      }
    } catch {
      // user gesture / unsupported -- ignore silently
    }
  }, []);

  // ----------------------------------------------------------------------
  // Tap handling: single tap on mobile toggles controls, double-tap on
  // left/right half skips +-10s with a brief ripple. On desktop we keep
  // the existing "click toggles play/pause" behaviour because there's
  // a separate cursor and the user can hover the controls anyway.
  // ----------------------------------------------------------------------
  const triggerSkip = useCallback((dir: "back" | "forward") => {
    skip(dir === "back" ? -10 : 10);
    setSkipFlash((prev) => ({ key: (prev?.key ?? 0) + 1, dir }));
  }, [skip]);

  const handleVideoPointerUp = useCallback((e: React.PointerEvent<HTMLVideoElement>) => {
    const isTouch = e.pointerType === "touch" || e.pointerType === "pen";
    if (!isTouch) {
      // Desktop mouse: behave like a single click -> toggle play/pause.
      togglePlay();
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const localX = e.clientX - rect.left;
    const third = rect.width / 3;
    const side: "left" | "right" | "center" =
      localX < third ? "left" : localX > rect.width - third ? "right" : "center";

    const now = performance.now();
    const last = lastTapRef.current;
    const DOUBLE_TAP_MS = 320;

    // Double tap on the same side -> skip.
    if (
      last &&
      now - last.at < DOUBLE_TAP_MS &&
      last.side === side &&
      side !== "center"
    ) {
      if (singleTapTimerRef.current) {
        window.clearTimeout(singleTapTimerRef.current);
        singleTapTimerRef.current = null;
      }
      triggerSkip(side === "left" ? "back" : "forward");
      lastTapRef.current = null;
      bumpIdle();
      return;
    }

    // First tap (or center tap). Schedule a single-tap action that
    // runs IF no second tap arrives within the window.
    lastTapRef.current = { at: now, x: localX, side };
    if (singleTapTimerRef.current) window.clearTimeout(singleTapTimerRef.current);
    singleTapTimerRef.current = window.setTimeout(() => {
      // Center tap toggles play; side tap toggles controls visibility.
      // Either way it's mobile-friendly: nothing happens accidentally
      // when the user is reaching for a specific control.
      if (side === "center") {
        togglePlay();
      } else {
        setControlsVisible((v) => !v);
      }
      lastTapRef.current = null;
      singleTapTimerRef.current = null;
    }, DOUBLE_TAP_MS);
  }, [togglePlay, triggerSkip, bumpIdle]);

  // Auto-clear the skip-flash ripple after its animation.
  useEffect(() => {
    if (!skipFlash) return;
    const t = window.setTimeout(() => setSkipFlash(null), 700);
    return () => window.clearTimeout(t);
  }, [skipFlash]);

  // MediaSession: surfaces play/pause/seek into the lock screen +
  // notification shade on Android, the Control Center on iOS, and the
  // media key keyboard hooks on desktop. Best-effort; we feature-detect
  // before registering and silently no-op on unsupported browsers.
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    const ms = navigator.mediaSession;
    ms.setActionHandler("play", () => { void videoRef.current?.play(); });
    ms.setActionHandler("pause", () => { videoRef.current?.pause(); });
    ms.setActionHandler("seekbackward", (e) => { skip(-(e.seekOffset ?? 10)); });
    ms.setActionHandler("seekforward",  (e) => { skip(e.seekOffset ?? 10); });
    try {
      ms.setActionHandler("seekto", (e) => {
        if (typeof e.seekTime === "number") seek(e.seekTime);
      });
    } catch { /* older browsers don't support seekto */ }
    return () => {
      ["play", "pause", "seekbackward", "seekforward", "seekto"].forEach((a) => {
        try { ms.setActionHandler(a as MediaSessionAction, null); } catch { /* ignore */ }
      });
    };
  }, [skip, seek]);

  // Push progress into MediaSession so the lock-screen scrubber tracks.
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    if (!duration || !Number.isFinite(duration)) return;
    try {
      navigator.mediaSession.setPositionState?.({
        duration,
        position: Math.min(currentTime, duration),
        playbackRate: rate,
      });
    } catch { /* some browsers throw on out-of-range; ignore */ }
  }, [currentTime, duration, rate]);

  // Reflect playing/paused on the MediaSession state -- this is what
  // the lock-screen play/pause button shows.
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    navigator.mediaSession.playbackState = isPlaying ? "playing" : "paused";
  }, [isPlaying]);

  // Mobile fullscreen UX: when the user enters fullscreen on a phone,
  // attempt to lock the orientation to landscape. The Screen Orientation
  // API is gated by an active fullscreen lifecycle, so we tie the call
  // to the fullscreenchange flow. iOS Safari doesn't support orientation
  // lock; we silently skip when unsupported.
  useEffect(() => {
    if (typeof window === "undefined") return;
    // The Screen Orientation API's `lock`/`unlock` aren't in the
    // baseline TS lib types yet, so we feature-detect through a
    // widened cast. The string "landscape" is broadly supported even
    // though TS's OrientationLockType doesn't include it (per-vendor).
    type OrientationApi = {
      lock?: (orientation: string) => Promise<void>;
      unlock?: () => void;
    };
    const orientation = (screen as Screen & { orientation?: ScreenOrientation }).orientation as
      | (ScreenOrientation & OrientationApi)
      | undefined;
    if (!orientation || typeof orientation.lock !== "function") return;
    const isCoarse = window.matchMedia("(pointer: coarse)").matches;
    if (!isCoarse) return;
    if (isFullscreen) {
      void orientation.lock("landscape").catch(() => {});
    } else {
      try { orientation.unlock?.(); } catch { /* ignore */ }
    }
  }, [isFullscreen]);

  // Keyboard shortcuts: Space, Arrow keys, F, M.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) return;
      if (e.code === "Space")          { e.preventDefault(); togglePlay(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); skip(10); }
      else if (e.key === "ArrowLeft")  { e.preventDefault(); skip(-10); }
      else if (e.key === "ArrowUp")    { e.preventDefault(); setVol(Math.min(1, volume + 0.1)); }
      else if (e.key === "ArrowDown")  { e.preventDefault(); setVol(Math.max(0, volume - 0.1)); }
      else if (e.key === "f" || e.key === "F") { e.preventDefault(); toggleFullscreen(); }
      else if (e.key === "m" || e.key === "M") { e.preventDefault(); toggleMute(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [togglePlay, skip, toggleFullscreen, toggleMute, setVol, volume]);

  // ----------------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------------
  const progressPct = duration ? (currentTime / duration) * 100 : 0;
  const bufferedPct = duration ? (bufferedEnd / duration) * 100 : 0;
  const supportsPip =
    typeof document !== "undefined" &&
    "pictureInPictureEnabled" in document &&
    !!(document as DocumentWithPip).pictureInPictureElement === false
      ? (document as unknown as { pictureInPictureEnabled: boolean }).pictureInPictureEnabled
      : false;

  return (
    <div className={cn("w-full max-w-5xl mx-auto px-3 sm:px-4", className)}>
      {topSlot ? <div className="mb-5">{topSlot}</div> : null}

      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
        ref={containerRef}
        className={cn(
          "relative w-full bg-black overflow-hidden shadow-lg",
          isFullscreen ? "rounded-none" : "rounded-xl aspect-video",
        )}
        onMouseMove={bumpIdle}
        onMouseLeave={() => isPlaying && setControlsVisible(false)}
        onTouchStart={bumpIdle}
      >
        <video
          key={src}
          ref={videoRef}
          src={src}
          className="w-full h-full bg-black"
          preload="metadata"
          playsInline
          crossOrigin="anonymous"
          // Custom tap handler: single tap on mobile toggles controls,
          // double tap left/right skips +-10s. See handleVideoPointerUp.
          onPointerUp={handleVideoPointerUp}
          onLoadedMetadata={(e) => {
            setDuration(e.currentTarget.duration);
            setIsLoading(false);
          }}
          onTimeUpdate={(e) => {
            const t = e.currentTarget.currentTime;
            setCurrentTime(t);
            onPlaybackProgress?.({ currentTime: t, duration: e.currentTarget.duration });
            if (storageKey && Math.floor(t) - lastSavedRef.current >= SAVE_EVERY_SECONDS) {
              localStorage.setItem(storageKey, String(t));
              lastSavedRef.current = Math.floor(t);
            }
          }}
          onProgress={(e) => {
            const v = e.currentTarget;
            if (v.buffered.length > 0) {
              setBufferedEnd(v.buffered.end(v.buffered.length - 1));
            }
          }}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onPlaying={() => { setIsLoading(false); setIsBuffering(false); }}
          onWaiting={() => { if (!isLoading) setIsBuffering(true); }}
          onCanPlay={() => { setIsLoading(false); setIsBuffering(false); }}
          onEnded={() => {
            setIsPlaying(false);
            if (storageKey) localStorage.removeItem(storageKey);
          }}
          onError={() => { setIsLoading(false); onError?.(); }}
        />

        {/* Double-tap skip ripple (mobile) -- a circular badge fades up
            on the corresponding half. Pointer-events:none so it doesn't
            block subsequent taps. */}
        <AnimatePresence>
          {skipFlash ? (
            <motion.div
              key={skipFlash.key}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.1 }}
              transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
              className={cn(
                "absolute inset-y-0 grid place-items-center pointer-events-none",
                skipFlash.dir === "back" ? "left-0 right-1/2" : "right-0 left-1/2",
              )}
            >
              <div className="flex flex-col items-center gap-1 text-white">
                <span className="grid place-items-center h-16 w-16 rounded-full bg-black/55 backdrop-blur-sm border border-white/15">
                  {skipFlash.dir === "back" ? (
                    <SkipBack className="h-7 w-7" />
                  ) : (
                    <SkipForward className="h-7 w-7" />
                  )}
                </span>
                <span className="text-xs font-medium tabular-nums">
                  {skipFlash.dir === "back" ? "-10s" : "+10s"}
                </span>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>

        {/* Center play affordance when paused, briefly. */}
        <AnimatePresence>
          {!isPlaying && !isLoading ? (
            <motion.button
              key="center-play"
              type="button"
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.92 }}
              transition={{ duration: 0.16, ease: [0.4, 0, 0.2, 1] }}
              onClick={togglePlay}
              aria-label="Play"
              className="absolute inset-0 grid place-items-center pointer-events-auto bg-black/15"
            >
              <span className="grid place-items-center h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-black/55 backdrop-blur-sm border border-white/15">
                <Play className="h-7 w-7 sm:h-9 sm:w-9 text-white ml-1" />
              </span>
            </motion.button>
          ) : null}
        </AnimatePresence>

        {/* Loading spinner */}
        {isLoading ? (
          <div className="absolute inset-0 grid place-items-center bg-black/60 backdrop-blur-[2px]">
            <div className="flex items-center gap-2 text-sm text-white">
              <Loader2 className="h-5 w-5 animate-spin text-brand" />
              <span>Loading video…</span>
            </div>
          </div>
        ) : null}

        {/* Buffering indicator (small, post-start) */}
        {isBuffering && !isLoading ? (
          <div className="absolute top-3 right-3 grid place-items-center h-8 w-8 rounded-full bg-black/50 backdrop-blur-sm">
            <Loader2 className="h-4 w-4 animate-spin text-white" />
          </div>
        ) : null}

        {/* Controls overlay ----------------------------------------- */}
        <AnimatePresence>
          {controlsVisible ? (
            <motion.div
              key="controls"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
              className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/45 to-transparent"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Seek + buffered */}
              <SeekBar
                current={currentTime}
                duration={duration}
                bufferedPct={bufferedPct}
                progressPct={progressPct}
                onSeek={seek}
              />

              <div className="px-2 sm:px-4 py-2 sm:py-3 flex items-center gap-1 sm:gap-2 text-white">
                <IconBtn label="Play/Pause" onClick={togglePlay}>
                  {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
                </IconBtn>
                <IconBtn label="Back 10s" onClick={() => skip(-10)} className="hidden sm:inline-flex">
                  <SkipBack className="h-4 w-4" />
                </IconBtn>
                <IconBtn label="Forward 10s" onClick={() => skip(10)} className="hidden sm:inline-flex">
                  <SkipForward className="h-4 w-4" />
                </IconBtn>

                <div className="flex items-center gap-1 sm:gap-2">
                  <IconBtn label={isMuted ? "Unmute" : "Mute"} onClick={toggleMute}>
                    {isMuted || volume === 0 ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                  </IconBtn>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={isMuted ? 0 : volume}
                    onChange={(e) => setVol(parseFloat(e.target.value))}
                    aria-label="Volume"
                    className={cn(
                      "hidden lg:block h-1 w-20 rounded-full appearance-none bg-white/30 cursor-pointer",
                      "[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3",
                      "[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white",
                      "[&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white",
                    )}
                  />
                </div>

                <span className="ml-1 text-[11px] sm:text-xs tabular-nums">
                  {formatTime(currentTime)} <span className="opacity-60">/ {formatTime(duration)}</span>
                </span>

                <div className="ml-auto flex items-center gap-1 sm:gap-2 relative">
                  <button
                    type="button"
                    onClick={() => setRateOpen((v) => !v)}
                    aria-haspopup="menu"
                    aria-expanded={rateOpen}
                    className="inline-flex items-center gap-1 px-2 sm:px-2.5 h-8 rounded-md text-xs tabular-nums hover:bg-white/10 transition-colors"
                  >
                    {rate}x <ChevronDown className="h-3 w-3" />
                  </button>
                  <AnimatePresence>
                    {rateOpen ? (
                      <motion.ul
                        role="menu"
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 4 }}
                        transition={{ duration: 0.12 }}
                        className="absolute right-0 bottom-[110%] min-w-[6.5rem] rounded-md border border-white/15 bg-black/90 backdrop-blur-md shadow-lg overflow-hidden"
                      >
                        {PLAYBACK_RATES.map((r) => (
                          <li key={r}>
                            <button
                              type="button"
                              onClick={() => changeRate(r)}
                              className={cn(
                                "w-full text-left px-3 py-1.5 text-xs hover:bg-white/10",
                                rate === r ? "text-brand" : "text-white",
                              )}
                            >
                              {r}x{r === 1 ? " (Normal)" : ""}
                            </button>
                          </li>
                        ))}
                      </motion.ul>
                    ) : null}
                  </AnimatePresence>

                  {supportsPip ? (
                    <IconBtn label={isPip ? "Exit Picture-in-Picture" : "Picture-in-Picture"} onClick={togglePip}>
                      <PictureInPicture2 className="h-4 w-4" />
                    </IconBtn>
                  ) : null}

                  <IconBtn label={isFullscreen ? "Exit fullscreen" : "Fullscreen"} onClick={toggleFullscreen}>
                    {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                  </IconBtn>
                </div>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

// --------------------------------------------------------------------------
function IconBtn({
  label,
  onClick,
  children,
  className,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={cn(
        "inline-flex items-center justify-center h-9 w-9 rounded-md text-white hover:bg-white/10 transition-colors",
        className,
      )}
    >
      {children}
    </button>
  );
}

function SeekBar({
  current,
  duration,
  bufferedPct,
  progressPct,
  onSeek,
}: {
  current: number;
  duration: number;
  bufferedPct: number;
  progressPct: number;
  onSeek: (v: number) => void;
}) {
  const trackRef = useRef<HTMLDivElement | null>(null);

  const seekAtClientX = useCallback((clientX: number) => {
    const el = trackRef.current;
    if (!el || !duration) return;
    const rect = el.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    onSeek(pct * duration);
  }, [duration, onSeek]);

  return (
    <div
      ref={trackRef}
      role="slider"
      aria-label="Seek"
      aria-valuemin={0}
      aria-valuemax={duration || 0}
      aria-valuenow={current || 0}
      tabIndex={0}
      onClick={(e) => seekAtClientX(e.clientX)}
      className="group relative h-2 sm:h-1.5 hover:h-2.5 transition-all bg-white/15 cursor-pointer"
    >
      <div
        className="absolute inset-y-0 left-0 bg-white/30"
        style={{ width: `${bufferedPct}%` }}
      />
      <div
        className="absolute inset-y-0 left-0 bg-brand"
        style={{ width: `${progressPct}%` }}
      >
        <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 h-3 w-3 rounded-full bg-white opacity-0 group-hover:opacity-100 shadow transition-opacity" />
      </div>
    </div>
  );
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
